/**
 * @file: reservation.service.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Frontend client for the euler backend `ReservationController`. The backend
 * injects tenancy + operator from identity, so requests carry neither.
 */

import { HttpClient } from '@euler/lib/request';
import { Reservation } from '@euler/model/reservation/Reservation';
import {
  ConvertReservationRequest,
  CreateReservationRequest,
  CreateReservationResult,
  ReservationListFilter,
  ReservationSorter,
  ReservationStats,
  TransitionReservationRequest,
  UpdateReservationRequest,
} from '@euler/model/reservation/types';

export class ReservationService {
  constructor(private readonly api: HttpClient) {}

  async list(
    filter: ReservationListFilter | undefined,
    offset: number,
    limit: number,
    sorter?: ReservationSorter,
  ): Promise<Reservation[]> {
    return await this.api
      .get()
      .url('/reservations', {
        filter: filter ? JSON.stringify(filter) : undefined,
        sorter: sorter ? JSON.stringify(sorter) : undefined,
        offset,
        limit,
      })
      .future();
  }

  // Per-status summary counts for the list tiles, in one grouped request. The
  // backend drops the filter's status field so each tile keeps its own total.
  async stats(
    filter: ReservationListFilter | undefined,
  ): Promise<ReservationStats> {
    return await this.api
      .get()
      .url('/reservations/stats', {
        filter: filter ? JSON.stringify(filter) : undefined,
      })
      .future();
  }

  async get(reservationId: string): Promise<Reservation> {
    return await this.api
      .get()
      .url('/reservations/:id', { id: reservationId })
      .future();
  }

  async create(
    request: CreateReservationRequest,
  ): Promise<CreateReservationResult> {
    return await this.api.post().url('/reservations').data(request).future();
  }

  async update(
    reservationId: string,
    request: UpdateReservationRequest,
  ): Promise<void> {
    await this.api
      .put()
      .url('/reservations/:id', { id: reservationId })
      .data(request)
      .future();
  }

  async transition(
    reservationId: string,
    request: TransitionReservationRequest,
  ): Promise<void> {
    await this.api
      .post()
      .url('/reservations/:id/transition', { id: reservationId })
      .data(request)
      .future();
  }

  async convert(
    reservationId: string,
    request: ConvertReservationRequest,
  ): Promise<void> {
    await this.api
      .post()
      .url('/reservations/:id/convert', { id: reservationId })
      .data(request)
      .future();
  }

  async delete(reservationId: string): Promise<void> {
    await this.api
      .delete()
      .url('/reservations/:id', { id: reservationId })
      .future();
  }
}
